import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const DEFAULT_COMPANY = (process.env.REACT_APP_COMPANY_NAME || "Tidsrapporten").trim();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Convert a backend-relative URL (e.g. "/api/uploads/branding/x.png") into an
// absolute URL the browser can fetch through the ingress.
const toAbsolute = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url}`;
};

const BrandingContext = createContext({
  companyName: DEFAULT_COMPANY,
  shortName: DEFAULT_COMPANY.toUpperCase(),
  logoUrl: null,
  loading: true,
  refresh: () => {},
});

export const BrandingProvider = ({ children }) => {
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings/branding");
      const name = (r.data?.company_name || DEFAULT_COMPANY).trim();
      setCompanyName(name);
      setLogoUrl(toAbsolute(r.data?.logo_url));
    } catch {
      setCompanyName(DEFAULT_COMPANY);
      setLogoUrl(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the browser tab title in sync with the brand.
  useEffect(() => {
    document.title = companyName
      ? `Tidsrapporten · ${companyName}`
      : "Tidsrapporten";
  }, [companyName]);

  const shortName = (companyName.split(/\s+/)[0] || companyName).toUpperCase();

  return (
    <BrandingContext.Provider value={{ companyName, shortName, logoUrl, loading, refresh: load }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
